// LoginPage — two-panel split mirroring staff_login.php (UI_AUDIT §2.37).
//
// Left panel  (52 %): dark brand surface, grid texture, radial glow.
//                     Tricivo wordmark + hero headline + stat tiles.
// Right panel (48 %): darkest background, centred form.
//
// Form logic is identical to the previous version: useForm / zodResolver,
// remember-me localStorage, redirect on success, axios error mapping.

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Lock, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../auth/useAuth';
import styles from './LoginPage.module.css';

const REMEMBER_KEY = 'tricivo_login_remember_username';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  remember: z.boolean().optional(),
});
type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '', remember: true },
  });

  useEffect(() => {
    try {
      const remembered = window.localStorage.getItem(REMEMBER_KEY);
      if (remembered) setValue('username', remembered);
    } catch {
      // ignore
    }
  }, [setValue]);

  useEffect(() => {
    if (isAuthenticated) {
      const target = decodeURIComponent(searchParams.get('redirect') ?? '/');
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const onSubmit = async (values: LoginFormValues) => {
    setSubmitError(null);
    try {
      if (values.remember) {
        try { window.localStorage.setItem(REMEMBER_KEY, values.username); } catch { /* ignore */ }
      } else {
        try { window.localStorage.removeItem(REMEMBER_KEY); } catch { /* ignore */ }
      }
      await login({ username: values.username, password: values.password });
      const target = decodeURIComponent(searchParams.get('redirect') ?? '/');
      navigate(target, { replace: true });
    } catch (err: unknown) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  return (
    <div className={styles.shell}>

      {/* ── Left: brand panel ── */}
      <div className={styles.panelLeft} aria-hidden="true">
        <div className={styles.leftInner}>

          <div className={styles.brand}>
            <div className={styles.brandIcon}>T</div>
            <span className={styles.brandName}>Tricivo</span>
          </div>

          <div className={styles.hero}>
            <div className={styles.eyebrow}>
              <span className={styles.eyebrowDot} />
              Operations Platform
            </div>
            <h2 className={styles.heroTitle}>
              Manage your<br />
              operations <span className={styles.heroAccent}>smarter</span>
            </h2>
            <p className={styles.heroDesc}>
              Unified platform for collections, skip management, weighbridge
              operations and more — all in one place.
            </p>
            <div className={styles.stats}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>8h</span>
                <span className={styles.statLabel}>Session length</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>Live</span>
                <span className={styles.statLabel}>Data sync</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>24/7</span>
                <span className={styles.statLabel}>Availability</span>
              </div>
            </div>
          </div>

          <p className={styles.tagline}>Tricivo Operations · All rights reserved</p>
        </div>
      </div>

      {/* ── Right: form panel ── */}
      <div className={styles.panelRight}>
        <div className={styles.formWrap}>

          <div className={styles.formHeader}>
            <h1 className={styles.formTitle}>Staff sign in</h1>
            <p className={styles.formSub}>Enter your credentials to continue</p>
          </div>

          {submitError ? (
            <div className={styles.alertDanger} role="alert">
              <AlertCircle size={15} aria-hidden />
              <span>{submitError}</span>
            </div>
          ) : null}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>

            <div className={styles.field}>
              <label htmlFor="username" className={styles.label}>Username</label>
              <div className={styles.inputWrap}>
                <User size={16} className={styles.inputIcon} aria-hidden />
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  autoFocus
                  placeholder="Your username"
                  className={`${styles.input} ${errors.username ? styles.invalid : ''}`}
                  {...register('username')}
                />
              </div>
              {errors.username ? (
                <div className={styles.fieldError}>{errors.username.message}</div>
              ) : null}
            </div>

            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>Password</label>
              <div className={styles.inputWrap}>
                <Lock size={16} className={styles.inputIcon} aria-hidden />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your password"
                  className={`${styles.input} ${errors.password ? styles.invalid : ''}`}
                  {...register('password')}
                />
              </div>
              {errors.password ? (
                <div className={styles.fieldError}>{errors.password.message}</div>
              ) : null}
            </div>

            <button type="submit" className={styles.submit} disabled={isSubmitting}>
              {isSubmitting ? (
                <><span className={styles.spinner} aria-hidden />Signing in…</>
              ) : 'Sign in'}
            </button>

          </form>

          <div className={styles.linksRow}>
            <label className={styles.checkbox}>
              <input type="checkbox" {...register('remember')} />
              Remember me
            </label>
            <Link to="/auth/forgot" className={styles.link}>Forgot password?</Link>
          </div>

          <div className={styles.divider} />
          <p className={styles.versionNote}>Tricivo Operations Platform</p>

        </div>
      </div>

    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401) return 'Invalid username or password.';
    if (status === 403) return 'Your account is not active. Contact your administrator.';
    if (status === 429) return 'Too many attempts. Please wait a minute and try again.';
    const body = err.response?.data as { message?: string } | undefined;
    if (body?.message) return body.message;
    if (err.message === 'Network Error') return 'Cannot reach the server. Check your connection.';
  }
  return 'Sign in failed. Please try again.';
}
